import styles from './DashBoardPageActions.module.scss';
import { Button } from '~/ui';
import cn from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from 'antd';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import { useToastError } from '~/utils/useToastError';
import FilterDropdown from '~/components/Shared/Dropdown/FilterDropdown';
import { useFormik } from 'formik';
import { AuthenticationContext } from '~/components/Authentication/AuthenticationProvider';
import { checkIfUserSuperAdmin } from '~/utils/getUserRole';
import { instance } from '~/utils/api/api';
import { useQuery } from '@tanstack/react-query';
import CalendarIcon from '~/assets/svg/newSvg/aside/calendar.svg?react';
import AssetIcon from '~/assets/svg/newSvg/aside/case.svg?react';
import CustomSelect from '~/components/Shared/CustomSelect/CustomSelect';
import { IIdsProps } from '../DashboardPage';

const END_DATES = {
  START_WEEK: dayjs()
    .subtract(7, 'day')
    .startOf('day')
    .format('YYYY-MM-DD HH:mm:ss'),
  START_MONTH: dayjs()
    .subtract(30, 'day')
    .startOf('day')
    .format('YYYY-MM-DD HH:mm:ss'),
  START_3_MONTH: dayjs()
    .subtract(90, 'day')
    .startOf('day')
    .format('YYYY-MM-DD HH:mm:ss'),
};

const CURRENT_DATE = dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss');

interface IAssets {
  id: number;
  name: string;
}

interface IFormik {
  start_date: null | Dayjs;
  end_date: null | Dayjs;
  assets_ids: IAssets[] | [];
  accomodations: IAssets[] | [];
  organizations: IAssets[];
}

const { RangePicker } = DatePicker;

const DashBoardPageActions = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  onClick,
}: {
  startDate: string;
  endDate: string;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  onClick: (data: IIdsProps) => void;
}) => {
  const [isOpenFilter, setIsOpenFilter] = useState(true);
  const [isAllAssedChecked, setIsAllAssedChecked] = useState<boolean>(false);
  const [assets, setAssets] = useState<IAssets[]>([]);
  const { user } = useContext(AuthenticationContext);
  const isSuperAdmin = checkIfUserSuperAdmin(user?.data.role_name);
  const { t } = useTranslation();
  const toastError = useToastError();

  const formik = useFormik<IFormik>({
    initialValues: {
      start_date: dayjs(startDate, 'YYYY-MM-DD HH:mm:ss'),
      end_date: dayjs(endDate, 'YYYY-MM-DD HH:mm:ss'),
      assets_ids: [],
      accomodations: [],
      organizations: [],
    },
    onSubmit: values => {
      setAssets(formik.values.assets_ids);
      const assetsIds = values.assets_ids.map(item => item.id);
      const accommodationIds = values.accomodations.map(item => item.id);
      const organizationIds = values.organizations.map(item => item.id);
      if (values.start_date && values.end_date) {
        setStartDate(
          values.start_date.startOf('day').format('YYYY-MM-DD HH:mm:ss')
        );
        setEndDate(values.end_date.endOf('day').format('YYYY-MM-DD HH:mm:ss'));
      }
      onClick({ assetsIds, accommodationIds, organizationIds });
      setIsOpenFilter(!isOpenFilter);
    },
  });

  const { data: assetsArr } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/assets');
      return data.data;
    },
    queryKey: ['assets'],
    onError: error => {
      toastError(error);
    },
  });

  const accomodationParams = {
    sort: '',
    perPage: 100,
    field: '',
    page: 1,
    asset_ids: formik.values.assets_ids.map(item => item.id),
  };

  const { data: accomodationsArr, isLoading: isAccomodationsLoading } =
    useQuery({
      queryFn: async () => {
        const { data } = await instance.get(`admin/accommodations`, {
          params: {
            ...accomodationParams,
          },
        });
        return data;
      },
      queryKey: ['accommodations', accomodationParams],
      enabled: !!formik.values.assets_ids.length,
      onError: error => {
        toastError(error);
      },
    });

  const accommodationIds = formik.values.accomodations.map(item => item.id);

  const { data: organizationsArr, isLoading: isOrganisationsLoading } =
    useQuery({
      queryFn: async () => {
        const { data } = await instance.get('admin/organization', {
          params: {
            sort: '',
            perPage: 100,
            field: '',
            page: 1,
            accommodation_ids: accommodationIds,
          },
        });
        return data;
      },
      queryKey: ['organizations', accommodationIds],
      onError: error => {
        toastError(error);
      },
    });

  useEffect(() => {
    formik.setFieldValue('organizations', []);
  }, [formik.values.accomodations]);

  useEffect(() => {
    if (!assetsArr || isSuperAdmin) return;
    setAssets(assetsArr);
    formik.setFieldValue('assets_ids', assetsArr);
  }, [assetsArr]);

  useEffect(() => {
    if (!startDate && !endDate) return;
    formik.setFieldValue(
      'start_date',
      dayjs(startDate, 'YYYY-MM-DD HH:mm:ss').startOf('day')
    );
    formik.setFieldValue(
      'end_date',
      dayjs(endDate, 'YYYY-MM-DD HH:mm:ss').endOf('day')
    );
  }, [startDate, endDate]);

  return (
    <div className={styles.dashboardPageActions}>
      <Button
        text={t('7_day')}
        className={cn(
          styles.dashboardButton,
          startDate === END_DATES.START_WEEK && endDate === CURRENT_DATE
            ? styles.dashboardButtonActive
            : ''
        )}
        onClick={() => {
          setStartDate(
            dayjs()
              .subtract(7, 'day')
              .startOf('day')
              .format('YYYY-MM-DD HH:mm:ss')
          );
          setEndDate(dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss'));
        }}
      />
      <Button
        text={t('30_day')}
        className={cn(
          styles.dashboardButton,
          startDate === END_DATES.START_MONTH && endDate === CURRENT_DATE
            ? styles.dashboardButtonActive
            : ''
        )}
        onClick={() => {
          setStartDate(
            dayjs()
              .subtract(30, 'day')
              .startOf('day')
              .format('YYYY-MM-DD HH:mm:ss')
          );
          setEndDate(dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss'));
        }}
      />
      <Button
        text={t('90_day')}
        className={cn(
          styles.dashboardButton,
          startDate === END_DATES.START_3_MONTH && endDate === CURRENT_DATE
            ? styles.dashboardButtonActive
            : ''
        )}
        onClick={() => {
          setStartDate(
            dayjs()
              .subtract(90, 'day')
              .startOf('day')
              .format('YYYY-MM-DD HH:mm:ss')
          );
          setEndDate(dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss'));
        }}
      />
      <div className={styles.dashboardPageDateWrapper}>
        <CalendarIcon className={styles.dateIcon} />
        <span className={styles.dashboardPageDates}>
          {dayjs(formik.values.start_date).format('DD.MM.YYYY')}-
          {dayjs(formik.values.end_date).format('DD.MM.YYYY')}
        </span>
        <div className={styles.assetsWrapper}>
          {!!assets.length && <AssetIcon />}
          {Array.isArray(assets) &&
            assets
              ?.map(item => (
                <span key={item.id} className={styles.dashboardPageDates}>
                  {item.name}
                </span>
              ))
              .slice(0, 3)}
          {assets.length > 3 && (
            <span className={styles.dashboardPageDates}>{'...'}</span>
          )}
        </div>
      </div>
      <div>
        <FilterDropdown toClose={isOpenFilter}>
          <form
            className={styles.dropdownContainer}
            onSubmit={formik.handleSubmit}
          >
            <span className={styles.dropdownTitle}>{t('filter')}</span>
            <div className={styles.dateWrapper}>
              <span className={styles.dateLabel}>{t('date')}</span>
              <RangePicker
                className={styles.date}
                format="DD.MM.YYYY"
                value={[formik.values.start_date, formik.values.end_date]}
                onChange={dates => {
                  if (dates && dates[0] && dates[1]) {
                    formik.setFieldValue('start_date', dates[0]);
                    formik.setFieldValue('end_date', dates[1]);
                  } else {
                    formik.setFieldValue('start_date', '');
                    formik.setFieldValue('end_date', '');
                  }
                }}
                disabledDate={currentDate => {
                  return (
                    currentDate && currentDate.isAfter(dayjs().endOf('day'))
                  );
                }}
                placeholder={[t('start_date'), t('end_date')]}
                onBlur={() => {
                  formik.setFieldTouched('start_date', true);
                  formik.setFieldTouched('end_date', true);
                }}
              />
            </div>
            <div className={styles.assetsWrapper}>
              {isSuperAdmin && (
                <CustomSelect
                  value={formik.values.assets_ids}
                  options={assetsArr}
                  placeholder={t('validate_asset')}
                  label={t('assets')}
                  required
                  disabled={isAllAssedChecked || !isSuperAdmin}
                  multiselect
                  onChange={value => {
                    formik.setFieldValue('assets_ids', value);
                    formik.setFieldValue('accomodations', []);
                  }}
                  hasCheckbox
                  checkboxLabel={t('all_assets')}
                  setIsCheckedAll={value => {
                    setIsAllAssedChecked(value);
                  }}
                />
              )}
              <CustomSelect
                value={formik.values.accomodations}
                options={accomodationsArr?.data}
                placeholder={t('accommodations_validate')}
                label={t('accommodation_facility')}
                disabled={
                  isAccomodationsLoading || !formik.values.assets_ids.length
                }
                multiselect
                onChange={value => formik.setFieldValue('accomodations', value)}
              />
              <CustomSelect
                value={formik.values.organizations}
                options={organizationsArr}
                label={t('organizations')}
                placeholder={t('validate_organizations')}
                disabled={isOrganisationsLoading}
                multiselect
                onChange={value => formik.setFieldValue('organizations', value)}
              />
            </div>
            <div className={styles.buttonWrapper}>
              <Button
                type="button"
                text={t('cancel')}
                className={styles.cancelBtn}
                onClick={() => setIsOpenFilter(!isOpenFilter)}
              />
              <Button
                type="submit"
                text={t('ready')}
                className={styles.readyBtn}
                disabled={
                  (!formik.values.start_date &&
                    !formik.values.end_date &&
                    !formik.values.assets_ids.length &&
                    !isAllAssedChecked) ||
                  (!isSuperAdmin &&
                    !formik.values.start_date &&
                    !formik.values.end_date)
                }
              />
            </div>
          </form>
        </FilterDropdown>
      </div>
    </div>
  );
};

export default DashBoardPageActions;
